import { Component, ViewChild, ElementRef, Input, SimpleChanges, AfterViewChecked } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { IntervalObservable } from 'rxjs/observable/IntervalObservable';
import { } from '@types/google.visualization';
import 'rxjs/add/operator/takeWhile';

import { TabComponentBase } from 'app/buffer-graph/models/tab-component-base';
import { StateInfo } from 'app/buffer-graph/models/assortment-structure/state-info';
import { BalanceStructure } from 'app/buffer-graph/models/assortment-structure/balance-structure';
import { BufferGraphService } from 'app/buffer-graph/services/buffer-graph.service';
import { SlickGridComponent } from 'app/slick-grid/slick-grid.component';
import { ISlickGridColumn } from 'app/slick-grid/interfaces/slick-grid-column';

@Component({
    selector: 'assortment-structure',
    templateUrl: './assortment-structure.component.html',
    styleUrls: ['./assortment-structure.component.css']
})
export class AssortmentStructureComponent extends TabComponentBase<any, any> implements AfterViewChecked {
    constructor(graphService: BufferGraphService, private readonly translateService: TranslateService) {
        super(graphService);

        this.translates = {};
        this.translateService.get(['ActivitiesByNetwork', 'BalanceStructure', 'BalanceEqualZero', 'BalanceInSaftey', 'BalanceInRed', 'BalanceInYellow', 'BalanceInGreen', 'BalanceInOverstock']).subscribe(t => {
            for (const key in t)
                if (t.hasOwnProperty(key))
                    this.translates[key] = t[key];
            this.translatesLoaded = true;
        });
    }

    @ViewChild('state')
    private stateRef: ElementRef;

    @ViewChild('balance')
    private balanceRef: ElementRef;

    @Input()
    protected isActive: boolean;

    @Input()
    private states: StateInfo[];

    @Input()
    private balanceStructure: BalanceStructure;

    @Input()
    private statesChartId: string;

    @Input()
    private balanceChartId: string;

    private readonly translates: { [key: string]: string };

    private readonly chartHeightCoefficient = 2.0/3.0;

    protected gridRef: SlickGridComponent;

    protected sku: string;

    protected warehouseCode: string;

    protected canSend: boolean;

    private statesChart: google.visualization.ChartWrapper;

    private createStatesChartAfterViewChecked: boolean;

    private statesDrawer: IntervalObservable;

    private balanceChart: google.visualization.ChartWrapper;

    private createBalanceChartAfterViewChecked: boolean;

    private balanceDrawer: IntervalObservable;

    private translatesLoaded: boolean;

    protected getExportData = this.getExportDataByDefault;

    ngOnChanges(changes: SimpleChanges): void {
        super.ngOnChanges(changes);

        if ((changes['states'] || changes['statesChartId']) && this.states && this.statesChartId /* && !this.statesDrawer*/)
            this.createStatesChartAfterViewChecked = true;

        if ((changes['balanceStructure'] || changes['balanceChartId']) && this.balanceStructure && this.balanceChartId /* && !this.statesDrawer*/)
            this.createBalanceChartAfterViewChecked = true;
    }

    ngAfterViewChecked(): void {
        if (this.createStatesChartAfterViewChecked) {
            this.createStatesChart();
            this.createStatesChartAfterViewChecked = false;
        }

        if (this.createBalanceChartAfterViewChecked) {
            this.createBalanceChart();
            this.createBalanceChartAfterViewChecked = false;
        }
    }

    protected onTabsResized(): boolean {
        if (!super.onTabsResized())
            return false;

        setTimeout(() => {
                if (this.statesChart) {
                    this.setStatesChartSize();
                    this.statesChart.draw();
                }

                if (this.balanceChart) {
                    this.setBalanceChartSize();
                    this.balanceChart.draw();
                }
            },
            1);

        return true;
    }

    private setStatesChartSize = (): void => {
        const options: google.visualization.PieChartOptions = this.statesChart.getOptions();
        this.stateRef.nativeElement.style.height = `${this.stateRef.nativeElement.offsetWidth * this.chartHeightCoefficient}px`;
        options.width = this.stateRef.nativeElement.offsetWidth;
        options.height = this.stateRef.nativeElement.offsetHeight;
    }

    private createStatesChart = (): void => {
        if (this.statesDrawer)
            return;

            let statesDrawed = false;
            this.statesDrawer = new IntervalObservable(10);
            this.statesDrawer.takeWhile(() => !this.destroyed && !statesDrawed).subscribe(() => {
                if (!this.stateRef || !this.translatesLoaded)
                    return;

                statesDrawed = true;

                const data = new google.visualization.DataTable();
                data.addColumn('string');
                data.addColumn('number');
                data.addRows(this.states.map(s => [s.state, s.count]));

                const options: google.visualization.PieChartOptions = {
                    title: this.translates['ActivitiesByNetwork'],
                    is3D: true,
                    colors: this.states.map(s => s.getColor())
                };

                this.statesChart = new google.visualization.ChartWrapper({
                    chartType: 'PieChart',
                    containerId: this.statesChartId,
                    options: options,
                    dataTable: data
                });

                this.setStatesChartSize();

                this.statesChart.draw();

                this.statesDrawer = null;
            });
    }

    private setBalanceChartSize = (): void => {
        const options: google.visualization.PieChartOptions = this.balanceChart.getOptions();
        this.balanceRef.nativeElement.style.height = `${this.balanceRef.nativeElement.offsetWidth * this.chartHeightCoefficient}px`;
        options.width = this.balanceRef.nativeElement.offsetWidth;
        options.height = this.balanceRef.nativeElement.offsetHeight;
    }

    private createBalanceChart = (): void => {
        if (this.balanceDrawer)
            return;

        let balanceDrawed = false;
        this.balanceDrawer = new IntervalObservable(10);
        this.balanceDrawer.takeWhile(() => !this.destroyed && !balanceDrawed).subscribe(() => {
            if (!this.balanceRef || !this.translatesLoaded)
                return;

            balanceDrawed = true;

            const rows: any[][] = [];
            const colors: string[] = [];
            for (const prop in this.balanceStructure) {
                if (!this.balanceStructure.hasOwnProperty(prop) || this.balanceStructure[prop] === 0)
                    continue;

                switch (prop) {
                case 'zeroBalance':
                    rows.push([this.translates['BalanceEqualZero'], this.balanceStructure.zeroBalance]);
                    colors.push('#000000');
                    break;
                case 'safetyStockZone':
                    rows.push([this.translates['BalanceInSaftey'], this.balanceStructure.safetyStockZone]);
                    colors.push('#800000');
                    break;
                case 'redZone':
                    rows.push([this.translates['BalanceInRed'], this.balanceStructure.redZone]);
                    colors.push('#FF0000');
                    break;
                case 'yellowZone':
                    rows.push([this.translates['BalanceInYellow'], this.balanceStructure.yellowZone]);
                    colors.push('#FFFF00');
                    break;
                case 'greenZone':
                    rows.push([this.translates['BalanceInGreen'], this.balanceStructure.greenZone]);
                    colors.push('#32CD32');
                    break;
                case 'overstockZone':
                    rows.push([this.translates['BalanceInOverstock'], this.balanceStructure.overstockZone]);
                    colors.push('#0000FF');
                    break;
                }
            }

            const data = new google.visualization.DataTable();
            data.addColumn('string');
            data.addColumn('number');
            data.addRows(rows);

            const options: google.visualization.PieChartOptions = {
                title: this.translates['BalanceStructure'],
                is3D: true,
                colors: colors
            };

            this.balanceChart = new google.visualization.ChartWrapper({
                chartType: 'PieChart',
                containerId: this.balanceChartId,
                options: options,
                dataTable: data
            });

            this.setBalanceChartSize();

            this.balanceChart.draw();

            this.balanceDrawer = null;
        });
    }
}



// WEBPACK FOOTER //
// ./src/app/buffer-graph/components/assortment-structure.component/assortment-structure.component.ts